import type { FC, ReactNode } from "react";
import { AppImage } from "@/src/components/NextImage";
import { cn } from "utils";
import type { IntroSlideTheme } from "./introSlidesConfig";

/** Left content panel — `md:` 50% split (Figma Intro Desktop ~496px on 1280). */
export const INTRO_DESKTOP_CONTENT_PANEL_CLASS =
  "relative z-10 flex min-h-dvh flex-1 flex-col px-[26px] pb-28 pt-safe md:w-1/2 md:max-w-[50%] md:shrink-0 md:px-[72px] md:pb-16 md:pt-16 md:overflow-y-auto";

interface IntroDesktopVisualPanelProps {
  slideIndex: number;
  theme: IntroSlideTheme;
  overlay?: ReactNode;
}

/** Right lifestyle / hero image panel — visible from `md:` breakpoint only. */
export const IntroDesktopVisualPanel: FC<IntroDesktopVisualPanelProps> = ({
  slideIndex,
  theme,
  overlay,
}) => {
  const desktopScrimLayers = theme.desktopScrimLayers ?? [];
  const desktopBackgroundImage =
    theme.desktopBackgroundImage ?? theme.backgroundImage;

  return (
    <div
      className={cn(
        "relative hidden min-h-dvh md:block md:w-1/2 md:shrink-0",
        !desktopBackgroundImage ? theme.fallbackClassName : undefined,
      )}
      data-testid={`intro-desktop-visual-panel-${slideIndex}`}
      data-layout="intro-desktop-visual"
    >
      {desktopBackgroundImage ? (
        <div className="absolute inset-2.5 overflow-hidden rounded-lg">
          <div className="relative size-full min-h-[calc(100dvh-20px)]">
            <AppImage
              src={desktopBackgroundImage}
              alt=""
              width={854}
              height={962}
              loading={slideIndex === 0 ? "eager" : "lazy"}
              className={cn(
                "pointer-events-none absolute inset-0 h-full w-full",
                theme.desktopBackgroundImageClassName ??
                  theme.backgroundImageClassName ??
                  "object-cover object-center",
              )}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            {desktopScrimLayers.map((layerClassName, index) => (
              <div
                key={`desktop-scrim-${slideIndex}-${index}`}
                className={cn(
                  "pointer-events-none absolute inset-0",
                  layerClassName,
                )}
                aria-hidden
              />
            ))}
            {overlay ? (
              <div
                className="absolute inset-0 flex items-center justify-center p-8"
                data-testid="intro-desktop-visual-overlay"
              >
                {overlay}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface IntroDesktopHeadlineStackProps {
  headline: ReactNode;
  subtitle: string;
}

export const IntroDesktopHeadlineStack: FC<IntroDesktopHeadlineStackProps> = ({
  headline,
  subtitle,
}) => (
  <div
    className="flex flex-col gap-[3px] md:gap-6"
    data-testid="intro-headline-block"
    data-node-id="96293:46171"
  >
    {headline}
    <p
      className="body_l md:body_xl text-content_inverse_primary whitespace-pre-wrap break-words"
      data-testid="intro-subtitle"
    >
      {subtitle}
    </p>
  </div>
);
